<template>
	<form class="property-form" enctype="multipart/form-data" @submit.prevent="submitForm">
		<div class="icon">
			<i class="fa-thin fa-file-contract"></i>
		</div>
		<errors-list :errors="errors"/>
		<div class="form-row">
			<default-field-box type="text" name="property[name]" :label="$t('forms.property_title')" :required="true" :has-error="errors?.['property.name']?.length"/>
		</div>
		<div class="form-row">
			<field-box name="property[type]" :label="$t('forms.property_type')" type="select" :options="typeOptions" :has-error="errors?.['property.type']?.length"/>
		</div>
		<div class="form-row">
			<field-box name="property[ownership]" :label="$t('forms.ownership')" type="select" :options="ownershipOptions" :has-error="errors?.['property.ownership']?.length"/>
		</div>
		<div class="form-row">
			<field-box name="property[value]" :label="$t('forms.property_value')" :placeholder="$t('forms.property_value_desc')" type="number" :has-error="errors?.['property.value']?.length"/>
		</div>
		<div class="form-row two-columns">
			<field-box name="property[purchase_date]" :label="$t('forms.purchase_date')" type="date" :has-error="errors?.['property.purchase_date']?.length"/>
			<field-box name="property[market_price]" :label="$t('forms.market_price')" type="number" :has-error="errors?.['property.market_price']?.length"/>
		</div>
		<div class="form-row two-columns">
			<field-box name="property[condition]" :label="$t('forms.condition')" type="select" :options="conditionOptions" :has-error="errors?.['property.condition']?.length"/>
			<field-box name="property[purchase_price]" :label="$t('forms.purchase_price')" type="number" :has-error="errors?.['property.purchase_price']?.length"/>
		</div>
		<div class="form-row">
			<field-box name="property[financing]" :label="$t('forms.financing')" type="select" :options="financingOptions" :has-error="errors?.['property.financing']?.length"/>
		</div>
		<div class="form-row" v-if="showDetailInfoTitle">
			<h4 class="form-section-title">{{ $t('forms.detail_info')}}</h4>
		</div>
		<property-car-fieldset v-if="type === 'car'" :errors="errors"/>
		<property-real-estate-fieldset v-else-if="type === 'real_estate'" :errors="errors"/>
		<property-land-fieldset v-else-if="type === 'land'" :errors="errors"/>
		<property-valuable-fieldset v-else-if="type === 'valuable'" :errors="errors"/>
		<property-intangible-asset-fieldset v-else-if="type === 'intangible_asset'" :errors="errors"/>
		<property-investment-fieldset v-else-if="type === 'investment'" :errors="errors"/>
		<div class="form-row">
			<h4 class="form-section-title">	{{ $t('routes.debts.title') }}</h4>
		</div>
		<template v-for="(item, index) in debts">
			<div class="form-row" :key="item.key">
				<field-box :name="`property[debt][${index}]`" :label="$t('forms.debt')" type="select" :options="debtOptions"/>
			</div>
			<div class="form-row">
				<button type="button" class="button style-6 small" @click.stop.prevent="removeDebt(item.key)">
					<span class="inner">{{ $t('forms.remove_debt') }}</span>
				</button>
			</div>
		</template>

		<div class="form-row">
			<button type="button" class="button style-2" @click.prevent="addDebt">
				<span class="inner">{{ $t('forms.add_debt') }}</span>
			</button>
		</div>
		<div class="form-actions">
			<button class="button style-4" type="submit" :class="loading ? 'loading' : ''">
				<span class="inner">{{ $t('forms.save') }}</span>
			</button>
			<button class="button style-1" type="button"
			        @click="$store.commit('HIDE_PROPERTY_CREATE_FORM')">
				<span class="inner">{{ $t('forms.cancel') }}</span>
			</button>
		</div>
	</form>
</template>

<script>
import DefaultFieldBox from "@/components/form-fields/DefaultFieldBox.vue";
import ErrorsList from "@/components/containers/ErrorsList.vue";
import FieldBox from "@/components/form-fields/FieldBox.vue";
import { trackEvent } from '@/analytics';
import PropertyCarFieldset from "@/components/fieldsets/PropertyCarFieldset.vue";
import PropertyRealEstateFieldset from "@/components/fieldsets/PropertyRealEstateFieldset.vue";
import PropertyLandFieldset from "@/components/fieldsets/PropertyLandFieldset.vue";
import PropertyValuableFieldset from "@/components/fieldsets/PropertyValuableFieldset.vue";
import PropertyIntangibleAssetFieldset from "@/components/fieldsets/PropertyIntangibleAssetFieldset.vue";
import PropertyInvestmentFieldset from "@/components/fieldsets/PropertyInvestmentFieldset.vue";
import clientConfirmMixin from "@/mixins/clientConfirmMixin";

export default {
	name: "PropertyCreateForm",
	mixins: [clientConfirmMixin],
	components: {
		FieldBox,
		ErrorsList,
		DefaultFieldBox,
		PropertyCarFieldset,
		PropertyRealEstateFieldset,
		PropertyLandFieldset,
		PropertyValuableFieldset,
		PropertyIntangibleAssetFieldset,
		PropertyInvestmentFieldset
	},
	data() {
		return {
			loading: false,
			errors: false,
			type: '',
			debtsCount: 0,
			debts: [],
			debtKey: 0,
		}
	},
	computed:{
		debtOptions(){
			let debts = this.$store.state.views.debts;
			let options = [
				{
					value: '',
					title: this.$t('forms.choose')
				}
			];

			debts.forEach(item => {
				options.push({
					value: `${item.type}_${item.id}`,
					title: item.name
				});
			});

			return options;
		},
		showDetailInfoTitle(){
			let options = [
				'car',
				'real_estate',
				'land',
				'valuable',
				'intangible_asset',
				'investment'
			];

			return options.includes(this.type);
		},
		typeOptions(){
			let options = [
				{
					value: '',
					title: this.$t('forms.choose')
				},
				{
					value: 'car',
					title: this.$t('forms.property_car'),
				},
				{
					value: 'real_estate',
					title: this.$t('forms.property_real_estate'),
				},
				{
					value: 'land',
					title: this.$t('forms.property_land'),
				},
				{
					value: 'valuable',
					title: this.$t('forms.property_valuable'),
				},
				{
					value: 'intangible_asset',
					title: this.$t('forms.property_intangible_asset'),
				},
				{
					value: 'investment',
					title: this.$t('forms.property_investment'),
				},
				{
					value: 'other',
					title: this.$t('forms.property_other'),
				}
			];

			return options;
		},
		ownershipOptions() {
			let options = [
				{
					value: '',
					title: this.$t('forms.choose')
				},
				{
					value: 'personal',
					title: this.$t('forms.ownership_personal'),
				},
				{
					value: 'join',
					title: this.$t('forms.ownership_join'),
				}
			];

			return options;
		},
		conditionOptions(){
			let options = [
				{
					value: '',
					title: this.$t('forms.choose')
				},
				{
					value: 'low',
					title: this.$t('forms.condition_low')
				},
				{
					value: 'average',
					title: this.$t('forms.condition_average')
				},
				{
					value: 'good',
					title: this.$t('forms.condition_good')
				},
				{
					value: 'excellent',
					title: this.$t('forms.condition_excellent')
				},
			];

			return options;
		},
		financingOptions(){
			let options = [
				{
					value: '',
					title: this.$t('forms.choose')
				},
				{
					value: 'paid',
					title: this.$t('forms.financing_paid')
				},
				{
					value: 'loan',
					title: this.$t('forms.financing_loan')
				},
			];

			return options;
		}
	},
	methods: {
		addDebt(){
			this.debts.push({
				key: this.debtKey,
			});
			this.debtKey++;
		},
		removeDebt(key){
			this.debts = this.debts.filter(item => {
				return key !== item.key;
			});
		},
		handleFieldChanged(data){
			switch (data.name){
				case 'property[type]':
					this.type = data.value;
					break;
			}
		},
		async submitForm(e) {
			if (!(await this.confirmAddAsChosenClient())) {
				return;
			}

			let form = e.target;
			let formData = new FormData(form);

			this.loading = true;
			trackEvent('form_submit', { form_name: 'property_create', module: 'properties' });
			this.axios.post('/api/properties', formData).then(response => {
				this._submitSuccess = true;
				trackEvent('form_submit_success', { form_name: 'property_create', module: 'properties' });
				form.reset();
				this.loading = false;
				this.$store.commit('ADD_PROPERTY', response.data.property);
				this.$store.commit('HIDE_PROPERTY_CREATE_FORM');
			}).catch(error => {
				trackEvent('form_submit_error', {
					form_name: 'property_create',
					module: 'properties',
					error_message: error.response && error.response.data ? error.response.data.message : error.message,
					error_status: error.response ? String(error.response.status) : null,
				});
				this.$swal.fire({
					icon: 'error',
					title: 'Oops',
					text: error.response.data.message
				});

				if (error.response.data.errors) {
					this.errors = error.response.data.errors;
				}

				this.loading = false;
			});
		},
	},
	mounted(){
		this._submitSuccess = false;
		trackEvent('form_open', { form_name: 'property_create', module: 'properties' });
		this.$root.$on('field-changed', this.handleFieldChanged);
	},
	beforeDestroy() {
		if (!this._submitSuccess) {
			trackEvent('form_close', { form_name: 'property_create', module: 'properties' });
		}
	},
	destroyed() {
		this.$root.$off('field-changed');
	}
}
</script>

<style scoped lang="scss">
fieldset {
	margin-bottom: 15px;
}
.property-form {
	display: flex;
	flex-direction: column;
	flex-grow: 1;
}

.form-group {
	label {
		font-size: 12px;
		text-align: center;
		text-transform: uppercase;
		font-weight: 600;
	}
}
.button {
	width: 100%;
}


.icon {
	text-align: center;
	font-size: 21px;
	margin-bottom: 10px;

	&:last-child {
		margin-bottom: 0;
	}
}

.form-row {
	margin-bottom: 15px;

	&:last-child {
		margin-bottom: 0;
	}

	&.two-columns {
		display: flex;
		justify-content: space-between;
		flex-direction: row;

		.field-box {
			width: calc(50% - 8px);
		}
	}
}

.form-actions {

	display: flex;
	flex-direction: column;
	margin-top: 15px;

	.button {
		margin-bottom: 10px;

		&:last-child {
			margin-bottom: 0;
		}
	}
}

@media($mobileS) {
	.form-actions {
		margin-top: 25px;
	}
	.form-row {
		margin-bottom: 10px;

		&.two-columns {
			flex-direction: column;

			.field-box {
				width: 100%;
				margin-bottom: 10px;

				&:last-child {
					margin-bottom: 0;
				}
			}
		}
	}
}
</style>